import { getClassifications, getClassification, ExtendedClassification } from "./getClassificationsFromSymbol";
import { Style } from "../../../ducks/style/style";
import { isSizeClassification } from "./classificationMapping";
import {
  ClusterSymbol,
  isClusterSymbol,
  isCombinedPolylineSymbol,
  isSizableSymbol,
  SimplePolylineSymbol,
  SvgPointSymbol,
} from "../../../interfaces/style";
import { getOffsetParameterValue } from "./classifyOffset";
import { setDefaultSymbolParameter } from "./updateSymbolParameter";

export class ClassificationManager {
  private classifications: ExtendedClassification[] = [];

  constructor(private style: Style, private resolution?: number) {
    this.collectClassifications();
  }

  public get values() {
    return this.classifications;
  }

  public find(predicate: (classification: ExtendedClassification) => boolean) {
    for (const classification of this.classifications) {
      if (predicate(classification)) {
        return classification;
      }
    }

    return null;
  }

  public map<T extends ValueOf<ExtendedClassification>>(callback: (classification: ExtendedClassification) => T) {
    return this.classifications.map(callback);
  }

  public reduce<T>(callback: (classifications: T, classification: ExtendedClassification) => T, initial: T) {
    return this.classifications.reduce(callback, initial);
  }

  public remove(filter: (classification: ExtendedClassification) => boolean) {
    const newClassifications: ExtendedClassification[] = [];

    for (const classification of this.classifications) {
      if (filter(classification)) {
        this.removeClassification(classification);
      } else {
        newClassifications.push(classification);
      }
    }

    this.classifications = newClassifications;
  }

  public hasClassifications() {
    return this.classifications.length > 0;
  }

  private collectClassifications() {
    const filtered = !this.resolution
      ? this.style.children
      : this.style.children?.filter(
          ({ minResolution, maxResolution }) =>
            !maxResolution || !this.resolution || (minResolution <= this.resolution && maxResolution > this.resolution),
        );
    const symbol = this.resolution ? filtered?.[0]?.symbol : this.style.symbol;

    if (filtered?.length) {
      this.classifications.push(getClassification(filtered, "symbol"));
    }

    if (symbol) {
      this.classifications.push(...getClassifications(symbol));
    }

    if (isClusterSymbol(symbol)) {
      this.classifications.push(...getClassifications((symbol as ClusterSymbol).singleObjectSymbol as SvgPointSymbol));
    }

    if (isCombinedPolylineSymbol(symbol)) {
      symbol.values.forEach(item => {
        this.classifications.push(...getClassifications(item as SimplePolylineSymbol));
      });
    }
  }

  private removeClassification(classification: ExtendedClassification) {
    const { parameter } = classification;

    if (!parameter) return;

    if (parameter === "symbol") {
      this.removeSymbolClassification();
    } else {
      this.removeSimpleClassification(parameter);
    }
  }

  private removeSimpleClassification(parameter: string) {
    setDefaultSymbolParameter(this.style.symbol, parameter);

    if (parameter === "width") {
      this.removeSimpleClassification("height");
    }

    if (isSizeClassification(parameter)) {
      this.removeOffsetClassification();
    }
  }

  private removeSymbolClassification() {
    this.style.children = null;
  }

  private removeOffsetClassification() {
    const { symbol } = this.style;

    if (isSizableSymbol(symbol) && symbol.offset) {
      symbol.offset = getOffsetParameterValue(symbol.offset);
    }

    this.style.symbol = symbol;
  }
}
